import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Receipt as InvoiceIcon,
  AccountBalance as BankIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Match } from "../services/matchingService";

interface MatchCardProps {
  match: Match;
  onApprove?: (matchId: number) => void;
  onReject?: (matchId: number) => void;
  onDelete?: (matchId: number) => void;
  loading?: boolean;
}

const MatchCard: React.FC<MatchCardProps> = ({
  match,
  onApprove,
  onReject,
  onDelete,
  loading = false,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getMatchTypeColor = (type: string) => {
    switch (type) {
      case "automatic":
        return "success";
      case "suggested":
        return "info";
      case "manual":
        return "default";
      default:
        return "default";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "success";
    if (score >= 70) return "info";
    if (score >= 50) return "warning";
    return "error";
  };

  const formatCriteria = (criteria: any) => {
    if (!criteria) return [];
    const labels: { key: string; label: string; icon: string }[] = [];

    if (criteria.valor)
      labels.push({
        key: "valor",
        label: `Valor: ${criteria.valor}`,
        icon: "💰",
      });
    if (criteria.data)
      labels.push({ key: "data", label: `Data: ${criteria.data}`, icon: "📅" });
    if (criteria.nif)
      labels.push({ key: "nif", label: `NIF: ${criteria.nif}`, icon: "🆔" });
    if (criteria.descricao)
      labels.push({
        key: "desc",
        label: `Desc: ${criteria.descricao}`,
        icon: "📝",
      });

    return labels;
  };

  const isPending = match.status === "pending";

  return (
    <Card
      sx={{
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
      }}
    >
      {loading && <LinearProgress />}

      <CardContent>
        {/* Header with Status and Score */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box>
            <Chip
              label={match.status}
              color={getStatusColor(match.status)}
              size="small"
              sx={{ mr: 0.5 }}
            />
            <Chip
              label={match.match_type}
              color={getMatchTypeColor(match.match_type)}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Chip
              label={`${match.confidence_score}%`}
              color={getScoreColor(match.confidence_score)}
              size="small"
            />
            <Tooltip title="Critérios de matching">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Invoice Info */}
        <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
          <InvoiceIcon color="primary" fontSize="small" />
          <Box flex={1}>
            <Typography variant="body2" fontWeight="bold">
              Fatura: {match.numero_fatura || `#${match.invoice_id}`}
            </Typography>
            {(match as any).empresa_nome && (
              <Typography
                variant="body2"
                color="primary"
                component="div"
                sx={{ mt: 0.3, mb: 0.3 }}
              >
                {(match as any).empresa_nome}
                {(match as any).empresa_fonte && (
                  <Chip
                    label={(match as any).empresa_fonte}
                    size="small"
                    sx={{ ml: 1, height: 16, fontSize: "0.65rem" }}
                    variant="outlined"
                    color={
                      (match as any).empresa_fonte === "AT" ? "success" : "info"
                    }
                  />
                )}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              €{Number(match.invoice_valor).toFixed(2)} •{" "}
              {dayjs(match.data_emissao).format("DD/MM/YYYY")}
              {match.nif_emitente && ` • NIF: ${match.nif_emitente}`}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Transaction Info */}
        <Box display="flex" alignItems="flex-start" gap={1} mb={1.5}>
          <BankIcon
            color={Number(match.transaction_valor) < 0 ? "error" : "success"}
            fontSize="small"
          />
          <Box flex={1}>
            <Typography variant="body2" fontWeight="bold">
              Movimento: €{Math.abs(Number(match.transaction_valor)).toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(match.data_movimento).format("DD/MM/YYYY")}
            </Typography>
            {match.transaction_descricao && (
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mt: 0.5 }}
                noWrap
                title={match.transaction_descricao}
              >
                {match.transaction_descricao}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Match Criteria */}
        {match.match_criteria && (
          <Box mt={1.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={0.5}
            >
              Critérios:
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {formatCriteria(match.match_criteria).map((item) => (
                <Chip
                  key={item.key}
                  label={`${item.icon} ${item.label}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Notes */}
        {match.notes && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1.5}
          >
            📝 {match.notes}
          </Typography>
        )}

        {/* Metadata */}
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1.5}
        >
          Criado por {match.matched_by} •{" "}
          {dayjs(match.matched_at).format("DD/MM/YYYY HH:mm")}
        </Typography>

        {match.approved_by && (
          <Typography variant="caption" color="text.secondary" display="block">
            Aprovado por {match.approved_by} •{" "}
            {dayjs(match.approved_at).format("DD/MM/YYYY HH:mm")}
          </Typography>
        )}
      </CardContent>

      {/* Actions */}
      {(isPending || onDelete) && (
        <CardActions>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box>
              {isPending && onApprove && (
                <Button
                  size="small"
                  startIcon={<ApproveIcon />}
                  onClick={() => onApprove(match.id)}
                  disabled={loading}
                  color="success"
                >
                  Aprovar
                </Button>
              )}
              {isPending && onReject && (
                <Button
                  size="small"
                  startIcon={<RejectIcon />}
                  onClick={() => onReject(match.id)}
                  disabled={loading}
                  color="error"
                >
                  Rejeitar
                </Button>
              )}
            </Box>
            {onDelete && (
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(match.id)}
                disabled={loading}
                color="error"
              >
                Eliminar
              </Button>
            )}
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export default MatchCard;
